import React, {useMemo} from 'react';
import styled from 'styled-components';
import {ColumnDef, Row, SortingFn} from '@tanstack/react-table';
import {Button, Icon} from '@forecast-it/design-system';
import {CellValue, RagState, ReportRow} from '../data/reportRowModel';
import {ReportDefinitionV1, FieldNode} from '../contracts/report-definition.types';
import {ReportTaxonomy} from '../contracts/report-taxonomy.types';
import {CellRenderer} from './cells/CellRenderer';
import {TaskNameCell} from './cells/TaskNameCell';
import {labelForField} from './fieldLabels';
import {isUnestimated} from './emptyValueLabels';
import {flattenFieldPaths} from '../data/definitionFields';
import {useIntl} from 'react-intl';
import {rowGrainOf} from '../reportGrain';
import {CellFormatter, createCellFormatter} from './cellDisplay';

export const EXPANDER_COLUMN_ID = '__expander';
export const FAVORITE_COLUMN_ID = '__favorite';

/** The task-name column renders through `TaskNameCell` so the name opens the task's modal. The
 * decision lives here rather than in `CellRenderer` because a task name arrives as an ordinary
 * text cell, indistinguishable from any other text column once the field path is gone. */
const TASK_NAME_COLUMN_ID = 'task.name';

/** Column ids that are structural (not field paths) and must be excluded from any
 * layout round-trip persisted back into the report definition. */
export const FIXED_COLUMN_IDS = [EXPANDER_COLUMN_ID, FAVORITE_COLUMN_ID];

const DEFAULT_COLUMN_WIDTH = 150;

/** Columns the flat default is too narrow for, with the width the Project Portfolio Standard Report
 * gives the same data. Both numbers are that report's own scale (`web-components/core/table/
 * Table.styled.js`): labels get EXTRA_LARGE, and the status note gets SUPER_LARGE, whose comment
 * there records 656px as 82 characters of description.
 *
 * At 150px a label chip is cut mid-word and a status note shows a few words, both of which read as
 * broken rather than as truncated. A reader can still drag either column narrower or wider; this is
 * only what they get before they touch it. Kept as an explicit map because nothing in the catalog
 * says how much room a field's values need. */
const DEFAULT_WIDTH_BY_COLUMN_ID = new Map<string, number>([
	['projectStatus.labels', 280],
	['projectStatus.ragNote', 656],
	// "25 Aug 2026, 16:11 GMT+10" on one line including the cell's horizontal padding, and a
	// typical approval note without truncation.
	['timeRegAuditEvent.timestamp', 220],
	['timeRegAuditEvent.reason', 320],
]);

/** Re-exported from the pure data module that owns it, so the table, the Columns dropdown and the
 * row mapper all read one definition of what a report's columns are. */
export {flattenFieldPaths};

/** Returns a field tree carrying `dottedKey`, creating only the segments it lacks and reusing every
 * node it already finds — the inverse of `flattenFieldPaths`. Reuse is the point: an existing `task`
 * root must GAIN a `deliverable` child, never leave the tree with a second `task` root, which the
 * report-definition wire format has no way to merge.
 *
 * Pure — the input tree is never mutated. The caller is React state (`definition.fields`), so an
 * in-place edit would change the definition without a re-render and leave the table and the Columns
 * dropdown disagreeing about which columns exist.
 *
 * Returns the input array BY REFERENCE when the path is already present, at every level. Callers
 * rely on that to tell "nothing changed" from "rebuilt to the same shape" — without it, re-adding a
 * column would set fresh state, mark the report dirty and trigger a refetch that changes nothing. */
export function ensureFieldPath(fields: FieldNode[], dottedKey: string): FieldNode[] {
	const [head, ...rest] = dottedKey.split('.');
	const existing = fields.find(node => node.name === head);

	if (rest.length === 0) {
		return existing ? fields : [...fields, {name: head}];
	}

	const children = ensureFieldPath(existing?.children ?? [], rest.join('.'));
	if (existing && children === existing.children) {
		return fields;
	}

	const replacement: FieldNode = {name: head, children};

	return existing ? fields.map(node => (node === existing ? replacement : node)) : [...fields, replacement];
}

/** Data-column ids for a report definition: EVERY leaf field path (column membership always comes
 * from `definition.fields`), ordered by `layout` when present — layout dictates order/width only,
 * never membership. Any leaf field absent from `layout` (an AI-generated definition whose layout is
 * a strict subset, or a column dropped from layout on `save()` because it was hidden) is appended in
 * field-tree order so it stays in the column model and can be switched back on in ColumnManager rather
 * than vanishing. Whether such a field also RENDERS is a separate question this function does not answer:
 * absence from a non-empty layout is how a hidden column is persisted, so `ReportBuilderContext` starts
 * it hidden when the definition was read back from storage (where a save wrote that layout) and visible
 * when it was freshly generated (where the subset layout means nothing about visibility). Membership and
 * visibility are separate concerns — this function owns membership only, so a hidden column is still
 * available to toggle. Layout entries
 * that no longer correspond to a leaf field are ignored. Excludes the fixed expander/favourite
 * columns — callers that need the full rendered column order wrap this with
 * `EXPANDER_COLUMN_ID`/`FAVORITE_COLUMN_ID` themselves. Shared by `useReportTableColumns` (column
 * model), `ColumnManager` (toggle list) and `ReportBuilderContext` (canonical table state) so the
 * membership+ordering rule lives in exactly one place. */
export function orderedDataColumnIds(definition: ReportDefinitionV1): string[] {
	const fieldOrder = flattenFieldPaths(definition.fields);
	const layout = definition.layout;
	if (!layout || layout.length === 0) {
		return fieldOrder;
	}

	const fieldPathSet = new Set(fieldOrder);
	const ordered = layout.map(entry => entry.field).filter(field => fieldPathSet.has(field));
	const inLayout = new Set(ordered);
	const remaining = fieldOrder.filter(path => !inLayout.has(path));
	return [...ordered, ...remaining];
}

function cellSortRank(value: CellValue | undefined, path?: string): number {
	if (value === undefined || value.kind === 'na' || isUnestimated(path, value)) {
		return 1;
	}
	return 0;
}

function cellSortLabel(value: CellValue): string {
	switch (value.kind) {
		case 'text':
		case 'date':
			return value.value;
		case 'project':
			return value.name;
		case 'status':
			return value.label;
		case 'persons':
			return value.persons.map(p => p.name).join(', ');
		case 'tag':
			return value.tags.map(t => t.name).join(', ');
		default:
			return '';
	}
}

/** Severity order for a RAG status cell, worst first. Sorting these by their display label - which
 * is what the generic label compare below would do - is wrong twice over: alphabetically the three
 * named states come out At Risk / Off Track / On Track, which is not an order anyone reading a
 * traffic light wants, and the order changes with the reader's language, so the same report sorts
 * differently in Danish. `unknown` sorts last because a project nobody has set a status on is the
 * least interesting row in a column that exists to surface the projects in trouble; `buildDataColumn`
 * pins the first header click to ascending, so that click puts the off-track projects on top and the
 * statusless ones at the bottom. A TOTAL record so a state added to `RagState` has to be placed here
 * rather than defaulting into some arbitrary slot. */
const RAG_SORT_ORDER: Record<RagState, number> = {
	off_track: 0,
	at_risk: 1,
	on_track: 2,
	unknown: 3,
};

/** The record above as the `Map` the comparator reads, so a `rag` spelled `toString` or
 * `constructor` cannot answer off the prototype chain with a function where a number is expected. */
const RAG_SORT_RANK = new Map<string, number>(Object.entries(RAG_SORT_ORDER));

/** Numeric magnitude of a cell for ordering: a duration by its unit-value (`minutes / 60`, which
 * matches the plain unit-value a unit-unknown duration degrades to as a `number`), a number by its
 * value, a boolean by `false`/`true` as `0`/`1`, everything else `null`. Sharing one scale lets a
 * duration and a degraded number sort against each other in a mixed-unit column instead of
 * comparing equal. */
function numericValue(cell: CellValue): number | null {
	if (cell.kind === 'duration') {
		return cell.minutes / 60;
	}
	if (cell.kind === 'number' || cell.kind === 'percent') {
		return cell.value;
	}
	if (cell.kind === 'boolean') {
		// No before Yes ascending; the header's first click puts the flagged rows last, then first.
		return cell.value ? 1 : 0;
	}
	return null;
}

/** Compares two `CellValue`s for a given column: na, and an unestimated estimate when `path` names
 * the estimate column, sort last; two RAG statuses compare by
 * severity (`RAG_SORT_ORDER`), not by their translated label; anything numeric (duration or number,
 * including a mix of the two) compares by numeric magnitude; everything else compares
 * lexicographically by its display label. */
export function compareCellValues(a: CellValue | undefined, b: CellValue | undefined, path?: string): number {
	const rankA = cellSortRank(a, path);
	const rankB = cellSortRank(b, path);
	if (rankA !== rankB) {
		return rankA - rankB;
	}
	if (rankA === 1) {
		return 0;
	}

	// a and b are both defined and non-na past this point.
	const definedA = a as CellValue;
	const definedB = b as CellValue;

	// A status column orders by what the states MEAN, not by how they happen to be spelled - see
	// `RAG_SORT_ORDER`. Only when both cells are statuses: a column holding a status beside some other
	// kind has no shared scale, and falls through to the label compare as before.
	if (definedA.kind === 'status' && definedB.kind === 'status') {
		const severityA = RAG_SORT_RANK.get(definedA.rag) ?? RAG_SORT_ORDER.unknown;
		const severityB = RAG_SORT_RANK.get(definedB.rag) ?? RAG_SORT_ORDER.unknown;
		return severityA - severityB;
	}

	const numA = numericValue(definedA);
	const numB = numericValue(definedB);
	if (numA !== null && numB !== null) {
		return numA - numB;
	}
	return cellSortLabel(definedA).localeCompare(cellSortLabel(definedB));
}

function makeSortingFn(path: string): SortingFn<ReportRow> {
	return (rowA: Row<ReportRow>, rowB: Row<ReportRow>): number =>
		compareCellValues(rowA.original.cells[path], rowB.original.cells[path], path);
}

function buildExpanderColumn(): ColumnDef<ReportRow> {
	return {
		id: EXPANDER_COLUMN_ID,
		header: '',
		size: 32,
		enableSorting: false,
		cell: ({row}) =>
			row.getCanExpand() ? (
				<Button
					iconOnly
					emphasis="low"
					icon={row.getIsExpanded() ? 'chevronUp' : 'chevronDown'}
					onPress={row.getToggleExpandedHandler()}
					aria-label="Toggle row expansion"
				/>
			) : null,
	};
}

/** Reset <button> so the favourite glyph renders in the dark on-light colour (#232334) the design
 * uses (the design: .icon-star), not the brand-blue a DS low-emphasis Button tints its icon. The
 * DS Icon owns the colour via newColor="onlight". */
const IconButton = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0;
	border: none;
	background: none;
	cursor: pointer;
`;

/** Per the design, the favourite star is its own column positioned near the far right (after the
 * RAG/status column, as the last column) — not fused into the leading expander column. */
function buildFavoriteColumn(onToggleFavorite: (rowId: string) => void): ColumnDef<ReportRow> {
	return {
		id: FAVORITE_COLUMN_ID,
		header: '',
		size: 40,
		enableSorting: false,
		cell: ({row}) => {
			// Only a row backed by a real task can be favourited, and `taskId` is the proof: a
			// deliverable tier has no task behind it, and neither has a row of another grain. Asking
			// for the id rather than excluding known types by name means a grain added later cannot
			// be offered a star that would favourite whatever task shares its row id.
			if (!row.original.taskId) {
				return null;
			}
			return (
				<IconButton onClick={() => onToggleFavorite(row.original.id)} aria-label="Toggle favorite">
					<Icon icon={row.original.favorite ? 'starFill' : 'star'} newColor={'onlight'} size={'s'} />
				</IconButton>
			);
		},
	};
}

/** Every data column, including a prose value like the project-status note, renders through
 * `CellRenderer`'s ordinary truncate-to-one-line-plus-tooltip path rather than wrapping in full.
 * This table enforces a fixed row height that a wrapped note cannot fit inside - even its first
 * wrapped line alone pushes a row past the limit, and there is no shorter compromise that keeps
 * two lines. Truncating does not lose the note: the full text is one hover away in the tooltip
 * `CellRenderer` shows once the line is actually cut off, rather than being cut off with no way
 * to read the rest. */
function buildDataColumn(
	path: string,
	label: string,
	width: number | undefined,
	formatCell: CellFormatter
): ColumnDef<ReportRow> {
	return {
		id: path,
		header: label,
		accessorFn: row => row.cells[path],
		cell: ctx =>
			path === TASK_NAME_COLUMN_ID ? (
				<TaskNameCell row={ctx.row.original} />
			) : (
				<CellRenderer
					value={formatCell(path, ctx.row.original.cells[path], ctx.row.original)}
					path={path}
					rowType={ctx.row.original.type}
				/>
			),
		enableSorting: true,
		sortingFn: makeSortingFn(path),
		// sortDescFirst: false ensures first header click sorts ascending. Without it, TanStack's
		// getAutoSortDir() detects the CellValue object (not a primitive) and defaults to descending.
		sortDescFirst: false,
		size: width ?? DEFAULT_WIDTH_BY_COLUMN_ID.get(path) ?? DEFAULT_COLUMN_WIDTH,
	};
}

export interface UseReportTableColumnsOptions {
	fieldLabels?: Record<string, string>;
	/** The grain's catalog: where a column's heading and its sortability come from for every grain
	 * but task, whose thirteen headings are pinned to their own shorter wording. */
	taxonomy?: ReportTaxonomy | null;
	onToggleFavorite: (rowId: string) => void;
}

/** Builds the TanStack column model for a report table from a report definition: one data
 * column per leaf field path — ordered and sized from `definition.layout` when present, else in
 * field-tree order — wrapped by a fixed leading expander column and a trailing favourite column. */
export function useReportTableColumns(
	definition: ReportDefinitionV1,
	{fieldLabels, taxonomy, onToggleFavorite}: UseReportTableColumnsOptions
): ColumnDef<ReportRow>[] {
	const intl = useIntl();
	return useMemo(() => {
		const layout = definition.layout;
		const orderedPaths = orderedDataColumnIds(definition);

		const widthByPath = new Map<string, number | undefined>();
		if (layout) {
			for (const entry of layout) {
				widthByPath.set(entry.field, entry.width);
			}
		}

		// Every column stays sortable, including the ones the catalog marks unsortable. That flag
		// says whether report-service can order BY the field, which is a different question from
		// whether this table can order the page it already holds - it registers a sorted row model
		// with no manual sorting, so it sorts the fetched cells itself. Honouring the flag here took
		// Status, Progress and Total hours away from a Project Status report, and sorting by status
		// is the main reason that report gets opened.
		// Display formatting only: `accessorFn` and the sorting functions still read the raw cell, so
		// a column orders by its real date rather than alphabetically by the formatted label.
		const formatCell = createCellFormatter(taxonomy, rowGrainOf(definition), {intl});
		const dataColumns = orderedPaths.map(path =>
			buildDataColumn(path, labelForField(path, taxonomy, fieldLabels), widthByPath.get(path), formatCell)
		);

		return [buildExpanderColumn(), ...dataColumns, buildFavoriteColumn(onToggleFavorite)];
	}, [definition, fieldLabels, taxonomy, onToggleFavorite, intl]);
}
